import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { divisionsAPI, teamsAPI, groundsAPI, conditionsAPI, matrixAPI, seriesAPI, fixturesAPI } from '../../services/api';
import { Spinner } from '../../components/ui';
import AssignConflictModal from '../../components/AssignConflictModal';
import toast from 'react-hot-toast';

// ─── Date Input (YYYY-MM-DD, supports typing + paste) ─────────────────────────
function DateInput({ value, onChange, style, ...props }) {
  const isValid = v => /^\d{4}-\d{2}-\d{2}$/.test(v);
  const [local, setLocal] = useState(value || '');
  const [invalid, setInvalid] = useState(false);
  // Sync from parent
  useEffect(() => { setLocal(value || ''); setInvalid(false); }, [value]);
  const commit = (v) => {
    const trimmed = v.trim();
    if (!trimmed) { onChange(''); setInvalid(false); return; }
    if (isValid(trimmed)) { onChange(trimmed); setInvalid(false); }
    else { setInvalid(true); }
  };
  return (
    <input type="text" className="input" placeholder="YYYY-MM-DD" {...props}
      style={{ fontSize: 11, fontFamily: "'Roboto Mono', monospace",
        borderColor: invalid ? 'var(--red)' : undefined, ...style }}
      value={local}
      onChange={e => setLocal(e.target.value)}
      onBlur={e => commit(e.target.value)}
      onKeyDown={e => { if (e.key === 'Enter') commit(e.target.value); }}
      onPaste={e => {
        e.stopPropagation();
        setTimeout(() => {
          const pasted = e.target.value;
          // Try to normalize common date formats to YYYY-MM-DD
          const m = pasted.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
          if (m) {
            const normalized = `${m[3]}-${m[1].padStart(2,'0')}-${m[2].padStart(2,'0')}`;
            setLocal(normalized);
            commit(normalized);
          } else {
            commit(pasted);
          }
        }, 0);
      }} />
  );
}

// ─── Constants ────────────────────────────────────────────────────────────────
const AUTO_CONFIRM_TYPES = new Set(['game_day','time_slot','bye_min','bye_max','team_specific']);
const BULK_TYPES         = new Set(['ground_rule','ground_booking','team_ground_limit']);
const BULK_LABELS        = { ground_rule:'Ground Rules (C5)', ground_booking:'Home Ground Assignments (C10)', team_ground_limit:'Team Ground Limits (C8)' };

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt12 = t => {
  if (!t) return '—';
  const [hStr, mStr] = String(t).split(':');
  const h = parseInt(hStr, 10);
  if (isNaN(h)) return t;
  return `${h > 12 ? h-12 : h === 0 ? 12 : h}:${mStr||'00'} ${h >= 12 ? 'PM' : 'AM'}`;
};

function describeCondition(c) {
  const v = c.value || {};
  switch (c.condition_type) {
    case 'game_day':                  return `Play days: ${(v.days||[]).join(', ') || 'not set'}`;
    case 'time_slot':                 return `Global time slots: ${(v.slots||[]).map(fmt12).join(', ') || 'not set'}`;
    case 'games_per_day':             return `Max ${v.max ?? '—'} game(s) per team per day`;
    case 'home_away':                 return `Home/Away balance: ${v.home||'—'} home, ${v.away||'—'} away${v.note?' — '+v.note:''}`;
    case 'bye_min':                   return `Min byes per team: ${v.min ?? '—'}`;
    case 'bye_max':                   return `Max consecutive byes: ${v.max ?? '—'}`;
    case 'max_gap':                   return `Max gap between games: ${v.days||'—'} days`;
    case 'ground_rule':               return `${v.ground_name||'Ground'}: max ${v.max_games_per_day||1}/day${v.division_restriction?' · '+v.division_restriction:''}`;
    case 'ground_division_restriction': return `${v.ground_name||v.text?.split(' ')[0]||'Ground'} restricted to ${(v.allowed_divisions||[]).join(', ')||v.text||'specific divisions'}`;
    case 'ground_booking':            return `${v.team_short||'Team'} hosts ${v.games_count||0} games @ ${v.ground_name||'ground'}`;
    case 'team_ground_limit':         return `${v.team_short||'Team'} max ${v.max_times||1} game(s) @ ${v.ground_name||'ground'}`;
    case 'division_monthly_cap':      return `${v.division_name||'Division'} in ${v.month||'—'}: max ${v.max_games||'—'} games`;
    case 'pool_format':               return `${v.division_name||'Division'} Pool ${v.pool||'A'} — seeded format, ${v.league_games||8} league games required`;
    case 'fixed_game_date':           return `${v.division_name||'Division'} must play on ${v.date||'—'}`;
    case 'team_specific':             return `Time override: ${v.division_name||'Division'} — ${(v.slots||v.times||[]).map(fmt12).join(', ')}`;
    case 'free_text':                 return `Note: ${v.text||v.note||JSON.stringify(v)}`;
    default:                          return v.text || `${c.condition_type}: ${JSON.stringify(v).substring(0,80)}`;
  }
}

function conditionCode(type) {
  const map = { game_day:'C1', time_slot:'C2', games_per_day:'C2', home_away:'C3',
    bye_min:'C4', bye_max:'C4', max_gap:'C4',
    ground_rule:'C5', ground_division_restriction:'C5',
    team_specific:'C7', team_ground_limit:'C8', ground_booking:'C10',
    fixed_game_date:'C11', division_monthly_cap:'Cap',
    pool_format:'Pool', free_text:'C16+' };
  return map[type] || type;
}

// ─── Nav Bar ──────────────────────────────────────────────────────────────────
function PageNav({ active }) {
  const navigate = useNavigate();
  return (
    <div style={{ display:'flex', gap:6, marginBottom:20 }}>
      {[
        { key:'generator', icon:'⚡', label:'Generator',   path:'/generator' },
        { key:'health',    icon:'🩺', label:'Team Health', path:'/team-health' },
        { key:'schedule',  icon:'📋', label:'Schedule',    path:'/schedule' },
      ].map(item => (
        <button key={item.key} type="button" onClick={() => navigate(item.path)}
          style={{ fontSize:12, fontWeight:600, padding:'6px 14px', borderRadius:6, cursor:'pointer',
            border:'1px solid', transition:'all .15s',
            background: active===item.key ? 'var(--accent)' : 'var(--surface)',
            color:       active===item.key ? 'white'        : 'var(--mid)',
            borderColor: active===item.key ? 'var(--accent)' : 'var(--border)' }}>
          {item.icon} {item.label}
        </button>
      ))}
    </div>
  );
}

// ─── Collapsible Section ──────────────────────────────────────────────────────
function Section({ icon, title, badge, badgeColor='var(--accent)', defaultOpen=false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="card" style={{ marginBottom:10 }}>
      <div onClick={() => setOpen(o=>!o)}
        style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 14px', cursor:'pointer', userSelect:'none' }}>
        <span style={{ fontSize:14 }}>{icon}</span>
        <span style={{ fontSize:12, fontWeight:800, color:'var(--text)', textTransform:'uppercase', letterSpacing:'.5px', flex:1 }}>{title}</span>
        {badge !== undefined && (
          <span style={{ fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:10,
            background:`color-mix(in srgb, ${badgeColor} 15%, transparent)`, color:badgeColor,
            border:`1px solid color-mix(in srgb, ${badgeColor} 30%, transparent)` }}>{badge}</span>
        )}
        <span style={{ fontSize:10, color:'var(--dim)' }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && <div style={{ padding:'0 14px 12px' }}>{children}</div>}
    </div>
  );
}

// ─── Step Header ─────────────────────────────────────────────────────────────
function StepHeader({ number, title, subtitle, status }) {
  const col = { done:'var(--accent)', active:'var(--blue)', locked:'var(--dim)' }[status] || 'var(--dim)';
  return (
    <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 0', marginBottom:8 }}>
      <div style={{ width:28, height:28, borderRadius:'50%', flexShrink:0, display:'flex',
          alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:800,
          background:`color-mix(in srgb, ${col} 15%, transparent)`, border:`2px solid ${col}`, color:col }}>
        {status==='done' ? '✓' : number}
      </div>
      <div>
        <div style={{ fontSize:13, fontWeight:800, color:status==='locked'?'var(--dim)':'var(--text)' }}>{title}</div>
        {subtitle && <div style={{ fontSize:10, color:'var(--dim)', marginTop:1 }}>{subtitle}</div>}
      </div>
      {status==='done'   && <span style={{ marginLeft:'auto', fontSize:10, color:'var(--accent)', fontWeight:700 }}>Complete ✓</span>}
      {status==='locked' && <span style={{ marginLeft:'auto', fontSize:10, color:'var(--dim)' }}>Complete previous step first</span>}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function GeneratorPage() {
  const navigate  = useNavigate();
  const logEndRef  = useRef(null);
  const wizardRef  = useRef(null);
  // Check if a saved config exists (for showing the Load button)
  const [hasSavedConfig, setHasSavedConfig] = useState(false);

  // Load saved config from server for current series — always resets state to
  // saved values (or defaults) so switching series doesn't leak previous data
  const loadSavedConfig = async (sid) => {
    const seriesId = sid || selSeries;
    if (!seriesId) return;
    try {
      const res = await seriesAPI.getGeneratorConfig(seriesId);
      const cfg = res.data || {};
      // Block auto-save from firing on the load-triggered state changes
      skipNextSaveRef.current = true;
      setDivOrder(cfg.divOrder || ['Premier','Division 1','Division 2','Division 3']);
      setSeededFloorDate(cfg.seededFloorDate || '2026-08-01');
      setSeededReserveCount(cfg.seededReserveCount || 5);
      setPoolAGamesReq(cfg.poolAGamesReq || 8);
      setConflictStrategy(cfg.conflictStrategy || 'fill_premier_first');
      setNoGameDays(cfg.noGameDays || []);
      setNoGameDaysTeam(cfg.noGameDaysTeam || []);
      setMandatoryGames(cfg.mandatoryGames || []);
      setConcludeBy(cfg.concludeBy || {});
      setMaxGapFail(cfg.maxGapFail || {});
      setHasSavedConfig(configHasData(cfg));
    } catch { /* no saved config yet */ }
  };

  // Determine whether a config has any user-entered data worth loading.
  // An empty config (savedAt only) shouldn't surface the Load button.
  const configHasData = (cfg) => {
    if (!cfg || typeof cfg !== 'object') return false;
    if (Array.isArray(cfg.noGameDays)      && cfg.noGameDays.length      > 0) return true;
    if (Array.isArray(cfg.noGameDaysTeam)  && cfg.noGameDaysTeam.length  > 0) return true;
    if (Array.isArray(cfg.mandatoryGames)  && cfg.mandatoryGames.length  > 0) return true;
    if (cfg.concludeBy  && Object.values(cfg.concludeBy).some(v => v))        return true;
    if (cfg.maxGapFail  && Object.values(cfg.maxGapFail).some(v => v))        return true;
    return false;
  };

  // Save current config to server for current series
  const saveConfig = async (showToast = false) => {
    if (!selSeries) return;
    try {
      const cfg = {
        divOrder, seededFloorDate, seededReserveCount, poolAGamesReq, conflictStrategy,
        noGameDays, noGameDaysTeam, mandatoryGames, concludeBy, maxGapFail,
        savedAt: new Date().toISOString(),
      };
      await seriesAPI.saveGeneratorConfig(selSeries, cfg);
      setHasSavedConfig(configHasData(cfg));
      if (showToast) toast.success('Config saved');
    } catch (err) { toast.error('Could not save config: ' + (err.response?.data?.error || err.message)); }
  };

  // Scroll position preservation — prevents jump-to-top on React re-renders
  // Strategy: find the actual scroll container on mount, then use a ref to save/restore
  // scroll position around every state update via useLayoutEffect
  const scrollContainerRef = useRef(null);
  const scrollPosRef = useRef(0);

  useEffect(() => {
    // Walk up from our root div to find the actual scrolling ancestor
    const findScroller = (el) => {
      if (!el || el === document.documentElement) return document.documentElement;
      const s = window.getComputedStyle(el);
      if ((s.overflowY === 'auto' || s.overflowY === 'scroll') && el.scrollHeight > el.clientHeight)
        return el;
      return findScroller(el.parentElement);
    };
    const container = findScroller(wizardRef.current?.parentElement);
    scrollContainerRef.current = container;

    // Disable scroll anchoring on the container — this is the key fix
    // overflow-anchor: none tells the browser NOT to adjust scroll on DOM changes
    if (container && container !== document.documentElement) {
      container.style.overflowAnchor = 'none';
    }
    document.documentElement.style.overflowAnchor = 'none';
    document.body.style.overflowAnchor = 'none';

    return () => {
      if (container && container !== document.documentElement) {
        container.style.overflowAnchor = '';
      }
      document.documentElement.style.overflowAnchor = '';
      document.body.style.overflowAnchor = '';
    };
  }, []);

  // Save scroll position before every render
  // useLayoutEffect runs synchronously after DOM mutations but before paint
  // Data
  const [seriesList,  setSeriesList]  = useState([]);
  const [selSeries,   setSelSeries]   = useState('');
  const [divisions,   setDivisions]   = useState([]);
  const [teams,       setTeams]       = useState([]);
  const [grounds,     setGrounds]     = useState([]);
  const [conditions,  setConditions]  = useState([]);
  const [matrixInfo,  setMatrixInfo]  = useState([]);
  const [availMap,    setAvailMap]    = useState({});
  const [loadingMeta, setLoadingMeta] = useState(false);

  // Wizard phase + step
  const [wizardPhase, setWizardPhase] = useState('idle');
  const [wizardStep,  setWizardStep]  = useState(1);

  // Step 1: condition status map — keyed by condId | 'bulk_TYPE' | 'section_KEY'
  const [condStatus,   setCondStatus]   = useState({});
  const [condOverride, setCondOverride] = useState({});
  const [overrideOpen, setOverrideOpen] = useState({});
  const [collapsed,    setCollapsed]    = useState({});

  // Extra conditions
  const [extraConds,  setExtraConds]  = useState([]);
  const [addCondMode, setAddCondMode] = useState(null);
  const [newCondType, setNewCondType] = useState('');
  const [newCondText, setNewCondText] = useState('');

  // No-game dates
  const [noGameDays, setNoGameDays] = useState([]);
  const [ngDiv,      setNgDiv]      = useState('');
  const [ngDate,     setNgDate]     = useState('');

  // Mandatory games
  const [mandatoryGames, setMandatoryGames] = useState([]);
  const [mgDiv,    setMgDiv]    = useState('');
  const [mgHome,   setMgHome]   = useState('');
  const [mgAway,   setMgAway]   = useState('');
  const [mgDate,   setMgDate]   = useState('');
  const [mgGround, setMgGround] = useState('');

  // Conclude-by dates per division
  const [concludeBy,     setConcludeBy]     = useState({});
  // Max gap hard fail — per division ceiling (days). Generation fails if any team exceeds this.
  const [maxGapFail,     setMaxGapFail]     = useState({}); // { divisionId: days }
  // No game days per team
  const [noGameDaysTeam, setNoGameDaysTeam] = useState([]); // [{id, teamId, teamShort, date}]
  const [ngtTeam, setNgtTeam] = useState('');
  const [ngtDate, setNgtDate] = useState('');
  const [ngMode, setNgMode]   = useState('division'); // 'division' | 'team'
  const [editOpen, setEditOpen] = useState({}); // condId → bool
  const [editDraft, setEditDraft] = useState({}); // condId → draft value object

  // Step 2: conflicts
  const [knownConflicts,      setKnownConflicts]      = useState([]);
  const [conflictResolutions, setConflictResolutions] = useState({});
  const [assignData,          setAssignData]          = useState({});
  const [conflictOpen,        setConflictOpen]        = useState({}); // kept for legacy compat
  const [allFixtures,         setAllFixtures]         = useState([]); // loaded for Assign modal availability
  const [assignModalKey,      setAssignModalKey]      = useState(null); // which conflict is open in modal
  const [rescheduleModal,     setRescheduleModal]     = useState(null); // nested: confirmed fixture being relocated to free a date
  const [seriesAvailability,  setSeriesAvailability]  = useState(null); // ground_availability rows for Assign modal filtering
  const [freedDate,           setFreedDate]           = useState(null); // date freed by a reschedule; auto-applied to parent Assign modal

  // Generation
  const [genLog,     setGenLog]     = useState([]);
  const [genResult,  setGenResult]  = useState(null);
  const [generating, setGenerating] = useState(false);

  // Control panel
  const [divOrder,           setDivOrder]           = useState(['Premier','Division 1','Division 2','Division 3']);
  const [seededFloorDate,    setSeededFloorDate]    = useState('2026-08-01');
  const [seededReserveCount, setSeededReserveCount] = useState(5);
  const [poolAGamesReq,      setPoolAGamesReq]      = useState(8);
  const [conflictStrategy,   setConflictStrategy]   = useState('fill_premier_first');
  const [dragDivIdx,         setDragDivIdx]         = useState(null);

  // Load
  useEffect(() => {
    seriesAPI.list().then(r => {
      const list = r.data||[];
      setSeriesList(list);
      if (list.length > 0) setSelSeries(list[0].id);
    });
  }, []);

  useEffect(() => { if (selSeries) { loadMeta(selSeries); loadSavedConfig(selSeries); } }, [selSeries]);

  // Auto-save config to server (debounced) whenever any control changes
  // Skip the very first render after a series load to avoid persisting unchanged defaults
  const skipNextSaveRef = useRef(false);
  useEffect(() => {
    if (!selSeries) return;
    if (skipNextSaveRef.current) { skipNextSaveRef.current = false; return; }
    const timer = setTimeout(() => { saveConfig(); }, 800);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [divOrder, seededFloorDate, seededReserveCount, poolAGamesReq, conflictStrategy,
      noGameDays, noGameDaysTeam, mandatoryGames, concludeBy, maxGapFail]);

  const loadMeta = async (sid) => {
    setLoadingMeta(true);
    try {
      const [divRes, teamRes, condRes] = await Promise.all([
        divisionsAPI.list(sid).then(r=>r.data||[]),
        teamsAPI.list({ series_id:sid }).then(r=>r.data||[]),
        conditionsAPI.list({ series_id:sid }).then(r=>r.data||r||[]),
      ]);
      setDivisions(divRes); setTeams(teamRes); setConditions(condRes);
      const freshGrounds = await groundsAPI.list().then(r=>r.data||r||[]);
      setGrounds(freshGrounds);
      const mInfo = await Promise.all(divRes.map(async d => {
        try { const mat = await matrixAPI.get(d.id).then(r=>r.data||[]); return { division:d, hasMatrix:mat.length>0, matrixCount:mat.length }; }
        catch { return { division:d, hasMatrix:false, matrixCount:0 }; }
      }));
      setMatrixInfo(mInfo);
      const avMap = {};
      // Build C10 exclusive ground map: groundId → games_count
      // C10 grounds (Grand Rapids, Toledo) are capped at games_count, not dates × maxPD
      const c10Map = {};
      condRes.filter(cr => cr.condition_type === 'ground_booking').forEach(cr => {
        const v = typeof cr.value === 'string' ? JSON.parse(cr.value) : (cr.value||{});
        if (v.ground_id && v.games_count)
          c10Map[v.ground_id] = (c10Map[v.ground_id]||0) + parseInt(v.games_count);
      });

      await Promise.all(freshGrounds.map(async g => {
        try {
          // C10 exclusive ground: use games_count directly (not dates × maxPD)
          if (c10Map[g.id] !== undefined) {
            avMap[g.id] = c10Map[g.id];
            return;
          }
          const av = await fetch(`/api/grounds/${g.id}/availability?series_id=${sid}`,
            { headers:{ Authorization:`Bearer ${localStorage.getItem('michca_token')}` } }).then(r=>r.json());
          const dates = (av.availability||av||[]).filter(a=>a.status==='available');
          // Find C5 ground rule to get max_games_per_day for this ground
          const groundRule = condRes.find(cr =>
            cr.condition_type === 'ground_rule' &&
            (cr.value?.ground_id === g.id || cr.value?.ground_name === g.name)
          );
          const maxPD = groundRule?.value?.max_games_per_day
            ? parseInt(groundRule.value.max_games_per_day)
            : (g.max_concurrent || 1);
          // Slots = available dates × games per day (matches Availability page)
          avMap[g.id] = dates.length * maxPD;
        } catch { avMap[g.id]=0; }
      }));
      setAvailMap(avMap);
      // Auto-confirm certain types
      const auto = {};
      condRes.forEach(cond => { if (AUTO_CONFIRM_TYPES.has(cond.condition_type)) auto[cond.id]='confirmed'; });
      setCondStatus(auto);
      setCondOverride({}); setOverrideOpen({}); setCollapsed({});
      // noGameDays, noGameDaysTeam, mandatoryGames, concludeBy, maxGapFail, divOrder
      // are restored by loadSavedConfig (runs in parallel). Resetting them here
      // races against that load and can overwrite saved config with empty values.
      setExtraConds([]);
      setKnownConflicts([]); setConflictResolutions({}); setAssignData({});
      setWizardPhase('idle'); setWizardStep(1); setGenLog([]); setGenResult(null);
    } catch { toast.error('Failed to load data'); }
    finally { setLoadingMeta(false); }
  };

  // Derived
  const condByType = type => conditions.filter(c=>c.condition_type===type);
  const playDays   = condByType('game_day')[0]?.value?.days || [];
  const timeSlots  = condByType('time_slot')[0]?.value?.slots || [];
  const poolFormatCond = conditions.find(c=>c.condition_type==='pool_format'&&c.value?.seeded_game===true);

  const perDivExpected = useMemo(() => divisions.map(d => {
    const gpt = d.games_per_team || 9;
    const divTeams = teams.filter(t=>t.division_id===d.id);
    const pools = (d.pools && d.pools.length > 0)
      ? d.pools.map(p => ({ pool: p, count: divTeams.filter(t => t.pool === p).length }))
      : null;
    let league;
    if (pools && pools.length > 0) {
      league = 0;
      pools.forEach(({ count: t }) => {
        if (t < 2) return;
        if (t % 2 === 0) {
          league += (gpt * t) / 2;
        } else {
          const leagueGames = gpt - 1;
          league += Math.floor(leagueGames * t / 2);
        }
      });
    } else {
      league = Math.floor((gpt * divTeams.length) / 2);
    }
    let seeded = 0;
    if (poolFormatCond && poolFormatCond.value?.division_id===d.id) {
      const n = divTeams.filter(t=>t.pool===(poolFormatCond.value?.pool||'A')).length;
      if (n%2!==0&&n>=3) seeded=(n-1)/2;
    }
    return { id:d.id, name:d.name, league, seeded, teams:divTeams.length };
  }), [divisions, teams, poolFormatCond]);

  const expectedLeague = perDivExpected.reduce((s,d)=>s+d.league,0);
  const expectedSeeded = perDivExpected.reduce((s,d)=>s+d.seeded,0);
  const expectedTotal  = expectedLeague + expectedSeeded;
  const totalAvail     = Object.values(availMap).reduce((a,b)=>a+b,0);

  const errors = [];
  if (!playDays.length)  errors.push('C1: Play days not configured');
  if (!timeSlots.length) errors.push('C2: Time slots not configured');
  if (teams.length < 2)  errors.push('Not enough teams (need ≥ 2)');
  if (totalAvail === 0)  errors.push('No ground availability set');
  const hasErrors = errors.length > 0;

  // Step 1 progress
  const bulkTypesPresent = [...BULK_TYPES].filter(t=>conditions.some(c=>c.condition_type===t));
  const individualConds  = conditions.filter(c=>!AUTO_CONFIRM_TYPES.has(c.condition_type)&&!BULK_TYPES.has(c.condition_type));
  const confirmedIndiv   = individualConds.filter(c=>condStatus[c.id]==='confirmed'||condStatus[c.id]==='override').length;
  const confirmedBulk    = bulkTypesPresent.filter(t=>
    condStatus[`bulk_${t}`]==='confirmed' ||
    conditions.filter(c=>c.condition_type===t).every(c=>condStatus[c.id]==='confirmed'||condStatus[c.id]==='override')
  ).length;
  const confirmedExtra   = extraConds.filter(c=>c.confirmed).length;
  const confirmedSects   = ['section_noGameDays','section_mandatory','section_concludeBy','section_maxGapFail'].filter(k=>condStatus[k]==='confirmed').length;
  const confirmedCount   = confirmedIndiv + confirmedBulk + confirmedExtra + confirmedSects;
  const totalCondCount   = individualConds.length + bulkTypesPresent.length + extraConds.length + 4;
  const step1Complete    = confirmedCount === totalCondCount && totalCondCount > 0;

  // Step 2 progress
  const allConflictKeys = knownConflicts.map(c=>`${c.home_id||c.home}:${c.away_id||c.away}`);
  const resolvedCount   = allConflictKeys.filter(k => conflictResolutions[k]).length;
  const step2Complete   = allConflictKeys.length===0 || allConflictKeys.every(k=>conflictResolutions[k]);

  useEffect(() => { if (logEndRef.current) logEndRef.current.scrollIntoView({ behavior:'smooth' }); }, [genLog]);

  const startWizard = () => {
    if (hasErrors) { toast.error('Fix errors in Pre-flight first'); return; }
    setWizardPhase('wizard'); setWizardStep(1); setGenLog([]); setGenResult(null);
  };

  const handleGenerate = async () => {
    debugger;
 setWizardPhase('generating');
    const addLog = (msg, type='info') => setGenLog(p=>[...p,{msg,type,id:Date.now()+Math.random()}]);
    try {
      // Save config before generating so it's always up to date
      saveConfig();
      addLog('Starting schedule generation…');
      const token = localStorage.getItem('michca_token');
      const res = await fetch(`/api/fixtures/generate/${selSeries}`, {
        method:'POST',
        headers:{ Authorization:`Bearer ${token}`, 'Content-Type':'application/json' },
        body: JSON.stringify({
          divisionOrder: divOrder,
          seededReservation:{ floorDate:seededFloorDate, slotsToReserve:seededReserveCount, poolGamesRequired:poolAGamesReq },
          conflictStrategy,
          noGameDays:     noGameDays.map(n=>({ divisionId:n.divisionId, date:n.date })),
          noGameDaysTeam: noGameDaysTeam.map(n=>({ teamId:n.teamId, date:n.date })),
          mandatoryGames: mandatoryGames.map(m=>({ divisionId:m.divisionId, homeTeamId:m.homeTeamId, awayTeamId:m.awayTeamId, date:m.date, groundId:m.groundId||null })),
          concludeBy,
          maxGapFail,
          conditionOverrides: Object.entries(condOverride).map(([id,ov])=>({ conditionId:id, override:ov.text })),
          extraConditions: extraConds.map(e=>({ text:e.text })),
        }),
      });
      const data = await res.json();
      (data.log||[]).forEach(e=>addLog(e.msg,e.type||'info'));
      data.errors?.forEach(e=>addLog(`ERROR (${e.division}): ${e.error}`,'error'));
      data.conflicts?.forEach(c=>addLog(`CONFLICT: ${c.home} vs ${c.away} — ${c.reason||'no slot'}`,'warn'));
      data.warnings?.forEach(w=>addLog(`WARNING: ${w}`,'warn'));
      addLog(`${data.total_fixtures||0} fixtures · ${data.conflicts?.length||0} conflicts · ${data.errors?.length||0} errors`,
        data.errors?.length?'error':data.conflicts?.length?'warn':'ok');
      setGenResult(data);
      if (data.conflicts?.length>0) {
        setKnownConflicts(data.conflicts);
        // Pre-load fixtures so the AssignConflictModal can compute team availability
        try {
          const fx = await fixturesAPI.list({ series_id: selSeries });
          setAllFixtures(fx.data || []);
        } catch (e) { /* non-fatal; modal will degrade gracefully */ }
        // Pre-load ground availability so suggestions respect per-ground open dates
        try {
          const av = await groundsAPI.getAllAvailability(selSeries);
          setSeriesAvailability(av.data || []);
        } catch (e) { setSeriesAvailability(null); }
      }
      setWizardPhase('done');
    } catch (err) { addLog(`Fatal: ${err.message}`,'error'); setWizardPhase('done'); }
    finally {
 }
  };

  const downloadExcel = async (combined) => {
    try {
      const token = localStorage.getItem('michca_token');
      const url = combined ? `/api/fixtures/export/excel/combined?series_id=${selSeries}` : `/api/fixtures/export/excel?series_id=${selSeries}`;
      const res = await fetch(url,{ headers:{ Authorization:`Bearer ${token}` } });
      if (!res.ok) { toast.error('Export failed'); return; }
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob); a.download = `schedule_${combined?'combined_':''}${selSeries}.xlsx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(a.href);
    } catch (err) { toast.error('Download failed: '+err.message); }
  };

  // Confirm helpers — all use e.preventDefault + e.stopPropagation to kill scroll jump
  const confirmCond = (e, id) => {
    e.preventDefault(); e.stopPropagation();
    setCondStatus(p=>({...p,[id]:'confirmed'}));
    setOverrideOpen(p=>({...p,[id]:false}));
    setCollapsed(p=>({...p,[id]:true}));
  };
  const confirmBulk    = (e, type) => { e.preventDefault(); e.stopPropagation(); setCondStatus(p=>({...p,[`bulk_${type}`]:'confirmed'})); };
  const confirmSection = (e, key)  => { e.preventDefault(); e.stopPropagation(); setCondStatus(p=>({...p,[key]:'confirmed'})); };

  // ── Condition Card ───────────────────────────────────────────────────────────
  // ── Save condition edit to DB ────────────────────────────────────────────────
  const saveCondEdit = async (cond, newValue) => {
    try {
      await conditionsAPI.update(cond.id, {
        condition_type: cond.condition_type,
        value: newValue,
        label: cond.label,
        sort_order: cond.sort_order,
        division_id: cond.division_id,
      });
      // Update local state
      setConditions(p => p.map(c => c.id === cond.id ? { ...c, value: newValue } : c));
      setEditOpen(p => ({ ...p, [cond.id]: false }));
      setEditDraft(p => { const n = { ...p }; delete n[cond.id]; return n; });
      setCollapsed(p => ({ ...p, [cond.id]: true }));
      toast.success('Condition updated');
    } catch (err) {
      toast.error('Failed to save: ' + (err.response?.data?.error || err.message));
    }
  };

  // ── Inline edit form per condition type ─────────────────────────────────────
  const CondEditForm = ({ cond }) => {
    const v = editDraft[cond.id] || { ...cond.value };
    const set = (field, val) => setEditDraft(p => ({ ...p, [cond.id]: { ...(p[cond.id] || cond.value), [field]: val } }));
    const draft = editDraft[cond.id] || cond.value;

    const formStyle = { marginTop: 8, padding: '10px 12px', background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 6 };
    const labelStyle = { fontSize: 9, fontWeight: 700, color: 'var(--dim)', textTransform: 'uppercase', marginBottom: 3 };
    const rowStyle = { display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' };
    const fieldStyle = { flex: '1 1 140px' };

    const buttons = (
      <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
        <button type="button" className="btn btn-primary btn-sm" style={{ fontSize: 11 }}
          onClick={e => { e.stopPropagation(); saveCondEdit(cond, draft); }}>
          Save
        </button>
        <button type="button" className="btn btn-ghost btn-sm" style={{ fontSize: 11 }}
          onClick={e => { e.stopPropagation(); setEditOpen(p => ({ ...p, [cond.id]: false })); setEditDraft(p => { const n = { ...p }; delete n[cond.id]; return n; }); setCollapsed(p => ({ ...p, [cond.id]: true })); }}>
          Cancel
        </button>
      </div>
    );

    switch (cond.condition_type) {
      case 'fixed_game_date':
        return (
          <div style={formStyle} onMouseDown={e => e.stopPropagation()}>
            <div style={rowStyle}>
              <div style={fieldStyle}>
                <div style={labelStyle}>Division</div>
                <select className="input select" style={{ fontSize: 11, width: '100%' }} value={draft.division_id || ''}
                  onChange={e => { const d = divisions.find(d => d.id === e.target.value); set('division_id', e.target.value); set('division_name', d?.name || ''); }}>
                  <option value="">Select…</option>
                  {divisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              <div style={fieldStyle}>
                <div style={labelStyle}>Date</div>
                <DateInput style={{ width: '100%' }} value={draft.date || ''}
                  onChange={v => { set('date', v); set('text', `${draft.division_name || 'Division'} must play on ${v}`); }} />
              </div>
            </div>
            {buttons}
          </div>
        );

      case 'division_monthly_cap':
        return (
          <div style={formStyle} onMouseDown={e => e.stopPropagation()}>
            <div style={rowStyle}>
              <div style={fieldStyle}>
                <div style={labelStyle}>Division</div>
                <select className="input select" style={{ fontSize: 11, width: '100%' }} value={draft.division_name || ''}
                  onChange={e => { set('division_name', e.target.value); set('text', `${e.target.value} in ${draft.month || ''} max ${draft.max_games || ''}`); }}>
                  <option value="">Select…</option>
                  {divisions.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
                </select>
              </div>
              <div style={fieldStyle}>
                <div style={labelStyle}>Month</div>
                <input type="month" className="input" style={{ fontSize: 11, width: '100%' }} value={draft.month || ''}
                  onChange={e => { set('month', e.target.value); set('text', `${draft.division_name || ''} in ${e.target.value} max ${draft.max_games || ''}`); }} />
              </div>
              <div style={{ flex: '0 0 80px' }}>
                <div style={labelStyle}>Max Games</div>
                <input type="number" className="input" style={{ fontSize: 11, width: '100%' }} value={draft.max_games ?? ''}
                  onChange={e => { set('max_games', parseInt(e.target.value) || 0); set('text', `${draft.division_name || ''} in ${draft.month || ''} max ${e.target.value}`); }} />
              </div>
            </div>
            {buttons}
          </div>
        );

      case 'pool_format':
        return (
          <div style={formStyle} onMouseDown={e => e.stopPropagation()}>
            <div style={rowStyle}>
              <div style={fieldStyle}>
                <div style={labelStyle}>Division</div>
                <select className="input select" style={{ fontSize: 11, width: '100%' }} value={draft.division_id || ''}
                  onChange={e => { const d = divisions.find(d => d.id === e.target.value); set('division_id', e.target.value); set('division_name', d?.name || ''); }}>
                  <option value="">Select…</option>
                  {divisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              <div style={{ flex: '0 0 80px' }}>
                <div style={labelStyle}>Pool</div>
                <select className="input select" style={{ fontSize: 11, width: '100%' }} value={draft.pool || ''}
                  onChange={e => set('pool', e.target.value)}>
                  <option value="A">A</option>
                  <option value="B">B</option>
                </select>
              </div>
              <div style={{ flex: '0 0 100px' }}>
                <div style={labelStyle}>League Games</div>
                <input type="number" className="input" style={{ fontSize: 11, width: '100%' }} value={draft.league_games ?? ''}
                  onChange={e => set('league_games', parseInt(e.target.value) || 0)} />
              </div>
            </div>
            {buttons}
          </div>
        );

      case 'ground_division_restriction':
        return (
          <div style={formStyle} onMouseDown={e => e.stopPropagation()}>
            <div style={rowStyle}>
              <div style={fieldStyle}>
                <div style={labelStyle}>Ground</div>
                <select className="input select" style={{ fontSize: 11, width: '100%' }} value={draft.ground_id || ''}
                  onChange={e => { const g = grounds.find(g => g.id === e.target.value); set('ground_id', e.target.value); set('ground_name', g?.name || ''); }}>
                  <option value="">Select…</option>
                  {grounds.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
              </div>
              <div style={fieldStyle}>
                <div style={labelStyle}>Allowed Divisions (comma-separated)</div>
                <input type="text" className="input" style={{ fontSize: 11, width: '100%' }} value={(draft.allowed_divisions || []).join(', ')}
                  onBlur={e => { set('allowed_divisions', e.target.value.split(',').map(s => s.trim()).filter(Boolean)); set('text', `${draft.ground_name || 'Ground'} → ${e.target.value}`); }} />
              </div>
            </div>
            {buttons}
          </div>
        );

      case 'max_gap':
        return (
          <div style={formStyle} onMouseDown={e => e.stopPropagation()}>
            <div style={rowStyle}>
              <div style={{ flex: '0 0 120px' }}>
                <div style={labelStyle}>Max Gap (days)</div>
                <input type="number" className="input" style={{ fontSize: 11, width: '100%' }} value={draft.days ?? ''}
                  onChange={e => set('days', parseInt(e.target.value) || 0)} />
              </div>
            </div>
            {buttons}
          </div>
        );

      case 'games_per_day':
        return (
          <div style={formStyle} onMouseDown={e => e.stopPropagation()}>
            <div style={rowStyle}>
              <div style={{ flex: '0 0 120px' }}>
                <div style={labelStyle}>Max Games Per Day</div>
                <input type="number" className="input" style={{ fontSize: 11, width: '100%' }} value={draft.max ?? ''}
                  onChange={e => set('max', parseInt(e.target.value) || 0)} />
              </div>
            </div>
            {buttons}
          </div>
        );

      case 'home_away':
        return (
          <div style={formStyle} onMouseDown={e => e.stopPropagation()}>
            <div style={rowStyle}>
              <div style={{ flex: '0 0 100px' }}>
                <div style={labelStyle}>Home Games</div>
                <input type="number" className="input" style={{ fontSize: 11, width: '100%' }} value={draft.home ?? ''}
                  onChange={e => set('home', e.target.value)} />
              </div>
              <div style={{ flex: '0 0 100px' }}>
                <div style={labelStyle}>Away Games</div>
                <input type="number" className="input" style={{ fontSize: 11, width: '100%' }} value={draft.away ?? ''}
                  onChange={e => set('away', e.target.value)} />
              </div>
              <div style={fieldStyle}>
                <div style={labelStyle}>Note</div>
                <input type="text" className="input" style={{ fontSize: 11, width: '100%' }} value={draft.note ?? ''}
                  onBlur={e => set('note', e.target.value)} />
              </div>
            </div>
            {buttons}
          </div>
        );

      default:
        // Fallback: free-text edit of JSON value
        return (
          <div style={formStyle} onMouseDown={e => e.stopPropagation()}>
            <div style={labelStyle}>Edit Value (JSON)</div>
            <textarea rows={3} className="input"
              style={{ width: '100%', fontSize: 11, resize: 'vertical', marginBottom: 6, fontFamily: "'Roboto Mono', monospace" }}
              defaultValue={JSON.stringify(draft, null, 2)}
              onBlur={e => { try { setEditDraft(p => ({ ...p, [cond.id]: JSON.parse(e.target.value) })); } catch {} }} />
            {buttons}
          </div>
        );
    }
  };

  const CondCard = ({ cond, index }) => {
    const st        = condStatus[cond.id];
    const isOpen    = !!overrideOpen[cond.id];
    const isCollapsed = !!collapsed[cond.id] && (st==='confirmed'||st==='override');
    const ov        = condOverride[cond.id] || {};

    if (isCollapsed) {
      return (
        <div style={{ display:'flex', alignItems:'center', gap:8, padding:'5px 10px', borderRadius:6,
            background:'rgba(0,200,150,.04)', border:'1px solid rgba(0,200,150,.15)', marginBottom:4, cursor:'pointer' }}
          onClick={()=>setCollapsed(p=>({...p,[cond.id]:false}))}>
          <span style={{ fontSize:9, color:'var(--accent)', fontWeight:700 }}>✓</span>
          <span style={{ fontSize:9, padding:'1px 5px', borderRadius:4, background:'rgba(74,144,217,.1)', color:'var(--blue)', fontWeight:700 }}>{conditionCode(cond.condition_type)}</span>
          <span style={{ fontSize:10, color:'var(--mid)', flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{describeCondition(cond)}</span>
          <span style={{ fontSize:9, color:'var(--dim)' }}>▼</span>
        </div>
      );
    }

    return (
      <div style={{ padding:'10px 12px', borderRadius:8, marginBottom:6,
        background: st==='confirmed'?'rgba(0,200,150,.06)':st==='override'?'rgba(245,158,11,.06)':'var(--surface)',
        border:`1px solid ${st==='confirmed'?'rgba(0,200,150,.25)':st==='override'?'rgba(245,158,11,.25)':'var(--border)'}` }}>
        <div style={{ display:'flex', alignItems:'flex-start', gap:10 }}>
          <div style={{ fontSize:10, fontWeight:800, minWidth:26, color:'var(--dim)', background:'var(--surface)',
              border:'1px solid var(--border)', borderRadius:5, padding:'2px 3px', textAlign:'center', marginTop:1, flexShrink:0 }}>
            {index+1}
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:3 }}>
              <span style={{ fontSize:9, padding:'2px 6px', borderRadius:5, background:'rgba(74,144,217,.12)', color:'var(--blue)', fontWeight:800 }}>{conditionCode(cond.condition_type)}</span>
              <span style={{ fontSize:11, color:'var(--text)', fontWeight:600 }}>{describeCondition(cond)}</span>
            </div>
            {st==='override' && ov.text && <div style={{ fontSize:10, color:'var(--gold)', fontStyle:'italic', marginTop:2 }}>Override: {ov.text}</div>}
            {isOpen && (
              <div style={{ marginTop:8, padding:'8px 10px', background:'var(--card)', border:'1px solid var(--border)', borderRadius:6 }}
                onMouseDown={e=>e.stopPropagation()}>
                <div style={{ fontSize:9, fontWeight:700, color:'var(--dim)', textTransform:'uppercase', marginBottom:5 }}>Override this condition</div>
                <div style={{ minHeight:54 }}>
                  <textarea rows={2} className="input"
                    placeholder="Describe your override"
                    style={{ width:'100%', fontSize:11, resize:'vertical', marginBottom:6, boxSizing:'border-box' }}
                    value={ov.text||''}
                    onMouseDown={e=>e.stopPropagation()}
                    onClick={e=>e.stopPropagation()}
                    defaultValue={ov.text||''}
                    onBlur={e=>{ setCondOverride(p=>({...p,[cond.id]:{...ov,text:e.target.value}})); }} />
                </div>
                <div style={{ display:'flex', gap:6 }}>
                  <button type="button" className="btn btn-primary btn-sm" style={{ fontSize:11 }}
                    onClick={e=>{ e.preventDefault(); e.stopPropagation(); setCondStatus(p=>({...p,[cond.id]:'override'})); setOverrideOpen(p=>({...p,[cond.id]:false})); setCollapsed(p=>({...p,[cond.id]:true})); }}>
                    Save Override
                  </button>
                  <button type="button" className="btn btn-ghost btn-sm" style={{ fontSize:11 }}
                    onClick={e=>{ e.preventDefault(); e.stopPropagation(); setOverrideOpen(p=>({...p,[cond.id]:false})); }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
            {editOpen[cond.id] && <CondEditForm cond={cond} />}
          </div>
          <div style={{ display:'flex', gap:5, flexShrink:0, alignItems:'center' }}>
            {!editOpen[cond.id] && (
              <>
                {st!=='confirmed'&&st!=='override' ? (
                  <button type="button" className="btn btn-sm"
                    style={{ fontSize:10, background:'rgba(0,200,150,.1)', border:'1px solid rgba(0,200,150,.3)', color:'var(--accent)' }}
                    onClick={e=>confirmCond(e,cond.id)}>
                    ✅ Confirm
                  </button>
                ) : (
                  <span style={{ fontSize:10, color:'var(--accent)', fontWeight:700, padding:'3px 5px' }}>✓</span>
                )}
                <button type="button" className="btn btn-ghost btn-sm" style={{ fontSize:10 }}
                  onClick={e=>{ e.preventDefault(); e.stopPropagation(); setEditOpen(p=>({...p,[cond.id]:true})); setEditDraft(p=>({...p,[cond.id]:{...cond.value}})); setOverrideOpen(p=>({...p,[cond.id]:false})); }}>
                  <span style={{ display:'inline-block', transform:'scaleX(-1)' }}>✏️</span> Edit
                </button>
              </>
            )}
            {editOpen[cond.id] && (
              <button type="button" className="btn btn-ghost btn-sm" style={{ fontSize:10, color:'var(--red)' }}
                onClick={e=>{ e.preventDefault(); e.stopPropagation(); setEditOpen(p=>({...p,[cond.id]:false})); setEditDraft(p=>{ const n={...p}; delete n[cond.id]; return n; }); setCollapsed(p=>({...p,[cond.id]:true})); }}>
                ✕ Close
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ── Bulk Condition Card ──────────────────────────────────────────────────────
  const BulkCondCard = ({ type }) => {
    const [expanded, setExpanded] = useState(true); // must be before any early return
    const items = conditions.filter(c=>c.condition_type===type);
    if (!items.length) return null;
    const st = condStatus[`bulk_${type}`];
    return (
      <div style={{ padding:'10px 12px', borderRadius:8, marginBottom:6,
          background:st==='confirmed'?'rgba(0,200,150,.06)':'var(--surface)',
          border:`1px solid ${st==='confirmed'?'rgba(0,200,150,.25)':'var(--border)'}` }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer' }} onClick={()=>setExpanded(o=>!o)}>
          <span style={{ fontSize:9, padding:'2px 6px', borderRadius:5, background:'rgba(74,144,217,.12)', color:'var(--blue)', fontWeight:800 }}>{conditionCode(type)}</span>
          <span style={{ fontSize:11, fontWeight:700, color:'var(--text)', flex:1 }}>
            {BULK_LABELS[type]} <span style={{ fontWeight:400, color:'var(--dim)' }}>({items.length})</span>
          </span>
          {st==='confirmed' && <span style={{ fontSize:10, color:'var(--accent)', fontWeight:700 }}>✓ Confirmed</span>}
          <span style={{ fontSize:9, color:'var(--dim)' }}>{expanded?'▲':'▼'}</span>
        </div>
        {expanded && (
          <div style={{ marginTop:8 }}>
            {items.map(item=>(
              <div key={item.id} style={{ fontSize:10, color:'var(--mid)', padding:'3px 0', borderBottom:'1px solid var(--border)', display:'flex', gap:6 }}>
                <span style={{ color:'var(--accent)', flexShrink:0 }}>·</span>
                <span>{describeCondition(item)}</span>
              </div>
            ))}
          </div>
        )}
        {st!=='confirmed' && (
          <button type="button" className="btn btn-sm"
            style={{ fontSize:10, marginTop:8, background:'rgba(0,200,150,.1)', border:'1px solid rgba(0,200,150,.3)', color:'var(--accent)' }}
            onClick={e=>confirmBulk(e,type)}>
            ✅ Confirm All {items.length} {BULK_LABELS[type]} entries
          </button>
        )}
      </div>
    );
  };

  // ── Section Card ─────────────────────────────────────────────────────────────
  const SectionCard = ({ sectionKey, title, icon, children }) => {
    const st = condStatus[sectionKey];
    return (
      <div style={{ padding:'10px 12px', borderRadius:8, marginBottom:6,
          background:st==='confirmed'?'rgba(0,200,150,.04)':'var(--surface)',
          border:`1px solid ${st==='confirmed'?'rgba(0,200,150,.2)':'var(--border)'}` }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
          <span style={{ fontSize:13 }}>{icon}</span>
          <span style={{ fontSize:11, fontWeight:700, color:'var(--text)', flex:1 }}>{title}</span>
          {st==='confirmed'
            ? <span style={{ fontSize:10, color:'var(--accent)', fontWeight:700 }}>✓ Confirmed</span>
            : <button type="button" className="btn btn-sm"
                style={{ fontSize:10, background:'rgba(0,200,150,.1)', border:'1px solid rgba(0,200,150,.3)', color:'var(--accent)' }}
                onClick={e=>confirmSection(e,sectionKey)}>
                ✅ Confirm
              </button>
          }
        </div>
        {children}
      </div>
    );
  };

  // ──────────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding:'0 0 60px' }}>
      <div style={{ padding:'16px 0 12px' }}>
        <PageNav active="generator" />
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <div style={{ fontSize:22, fontWeight:900, color:'var(--text)' }}>⚡ Schedule Generator</div>
            <div style={{ fontSize:12, color:'var(--mid)', marginTop:2 }}>Review conditions → resolve conflicts → confirm → generate</div>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
            <label style={{ fontSize:10, fontWeight:700, color:'var(--dim)', textTransform:'uppercase' }}>Series</label>
            <select className="input select" style={{ width:200, fontSize:13, fontWeight:600 }}
              value={selSeries} onChange={e=>setSelSeries(e.target.value)}>
              {seriesList.map(s=><option key={s.id} value={s.id}>{s.name||s.short_name}</option>)}
            </select>
          </div>
        </div>
      </div>

      {loadingMeta ? (
        <div style={{ display:'flex', justifyContent:'center', padding:60 }}><Spinner size={28}/></div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'400px 1fr', gap:16, alignItems:'start' }}>

          {/* LEFT */}
          <div>
            <Section icon="🔍" title="Pre-flight Check" defaultOpen={true}
              badge={hasErrors?`${errors.length} error${errors.length>1?'s':''}`:'Ready'}
              badgeColor={hasErrors?'var(--red)':'var(--accent)'}>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8, marginBottom:12 }}>
                {[
                  { label:'Expected', value:expectedTotal, color:'#00C896', icon:'📋',
                    sub: expectedSeeded>0?`${expectedLeague} league + ${expectedSeeded} seeded`:`${expectedLeague} league` },
                  { label:'Teams', value:teams.length, color:'#9B59B6', icon:'👥', sub:`${divisions.length} divs` },
                  { label:'Avail Slots', value:totalAvail, icon:'🏟',
                    color:totalAvail>=expectedTotal?'#4A90D9':'#EF4444',
                    sub:totalAvail>=expectedTotal?`+${totalAvail-expectedTotal} surplus`:`${expectedTotal-totalAvail} short ⚠` },
                ].map(s=>(
                  <div key={s.label} style={{ background:`color-mix(in srgb, ${s.color} 8%, transparent)`,
                      border:`1px solid color-mix(in srgb, ${s.color} 25%, transparent)`,
                      borderRadius:8, padding:'10px', position:'relative', overflow:'hidden' }}>
                    <div style={{ position:'absolute', top:6, right:8, fontSize:16, opacity:.2 }}>{s.icon}</div>
                    <div style={{ fontSize:8, color:s.color, fontWeight:800, textTransform:'uppercase', letterSpacing:'.5px', marginBottom:2 }}>{s.label}</div>
                    <div style={{ fontSize:20, fontWeight:900, color:s.color, fontFamily:"'Roboto Mono',monospace", lineHeight:1 }}>{s.value}</div>
                    <div style={{ fontSize:8, color:s.color, opacity:.7, marginTop:2 }}>{s.sub}</div>
                  </div>
                ))}
              </div>
              {errors.map((e,i)=>(
                <div key={i} style={{ display:'flex', gap:8, padding:'5px 0', borderBottom:'1px solid var(--border)', fontSize:11 }}>
                  <span style={{ color:'var(--red)', fontWeight:700 }}>✕</span>
                  <span style={{ color:'var(--red)' }}>{e}</span>
                </div>
              ))}
              {!hasErrors && <div style={{ fontSize:11, color:'var(--accent)', fontWeight:600, marginTop:6 }}>✅ All pre-flight checks passed</div>}
            </Section>

            <Section icon="🎛" title="Control Panel" badge="Optional" badgeColor="var(--blue)">
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:9, fontWeight:800, color:'var(--dim)', textTransform:'uppercase', marginBottom:6 }}>Division Scheduling Order</div>
                {divOrder.map((div,idx)=>{
                  const cols={'Premier':'#F5A623','Division 1':'#4A90D9','Division 2':'#9B59B6','Division 3':'#7B68EE'};
                  const col=cols[div]||'var(--mid)'; const isP=div==='Premier';
                  return (
                    <div key={div} draggable={!isP}
                      onDragStart={()=>!isP&&setDragDivIdx(idx)} onDragOver={e=>e.preventDefault()}
                      onDrop={()=>{ if(dragDivIdx===null||dragDivIdx===idx)return; const a=[...divOrder];const[m]=a.splice(dragDivIdx,1);a.splice(idx,0,m);const pi=a.indexOf('Premier');if(pi>0){a.splice(pi,1);a.unshift('Premier');}setDivOrder(a);setDragDivIdx(null); }}
                      onDragEnd={()=>setDragDivIdx(null)}
                      style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 10px', marginBottom:3,
                        background:`color-mix(in srgb, ${col} 8%, transparent)`, border:`1px solid color-mix(in srgb, ${col} 25%, transparent)`,
                        borderRadius:6, cursor:isP?'default':'grab' }}>
                      <span style={{ fontSize:10, color:'var(--dim)', minWidth:14 }}>{isP?'🔒':'⠿'}</span>
                      <span style={{ fontSize:11, fontWeight:800, color:col, minWidth:14 }}>#{idx+1}</span>
                      <span style={{ fontSize:11, fontWeight:600, color:'var(--text)', flex:1 }}>{div}</span>
                    </div>
                  );
                })}
              </div>
              <div>
                <div style={{ fontSize:9, fontWeight:800, color:'var(--dim)', textTransform:'uppercase', marginBottom:6 }}>Seeded Game Controls</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                  <div><div style={{ fontSize:9, color:'var(--dim)', marginBottom:2 }}>Floor date</div><DateInput style={{ width:'100%' }} value={seededFloorDate} onChange={v=>setSeededFloorDate(v)} /></div>
                  <div><div style={{ fontSize:9, color:'var(--dim)', marginBottom:2 }}>Reserve slots</div><input type="number" className="input" style={{ width:'100%', fontSize:11 }} min={1} max={10} defaultValue={seededReserveCount} onBlur={e=>setSeededReserveCount(parseInt(e.target.value)||5)} /></div>
                  <div><div style={{ fontSize:9, color:'var(--dim)', marginBottom:2 }}>Pool A games required</div><input type="number" className="input" style={{ width:'100%', fontSize:11 }} min={1} max={12} defaultValue={poolAGamesReq} onBlur={e=>setPoolAGamesReq(parseInt(e.target.value)||8)} /></div>
                  <div><div style={{ fontSize:9, color:'var(--dim)', marginBottom:2 }}>Conflict strategy</div>
                    <select className="input select" style={{ width:'100%', fontSize:11 }} value={conflictStrategy} onChange={e=>setConflictStrategy(e.target.value)}>
                      <option value="fill_premier_first">Fill premier first</option>
                      <option value="fill_nearest_date">Fill nearest date</option>
                    </select>
                  </div>
                </div>
              </div>
            </Section>
          </div>

          {/* RIGHT: Wizard */}
          <div ref={wizardRef}>

            {/* IDLE */}
            {wizardPhase==='idle' && (
              <div className="card" style={{ padding:24 }}>
                <div style={{ textAlign:'center', marginBottom:20 }}>
                  <div style={{ fontSize:36, marginBottom:8 }}>⚡</div>
                  <div style={{ fontSize:16, fontWeight:800, color:'var(--text)', marginBottom:6 }}>Ready to Generate</div>
                  <div style={{ fontSize:12, color:'var(--mid)', lineHeight:1.6 }}>Click below to start the wizard. Review conditions, resolve conflicts, then confirm.</div>
                </div>
                {hasErrors && <div style={{ padding:'8px 12px', borderRadius:6, background:'rgba(239,68,68,.08)', border:'1px solid rgba(239,68,68,.2)', fontSize:11, color:'var(--red)', marginBottom:12 }}>✕ Fix {errors.length} error(s) in Pre-flight before generating</div>}
                <button type="button" className="btn btn-primary"
                  style={{ width:'100%', justifyContent:'center', fontSize:14, fontWeight:700, padding:'12px 0' }}
                  onClick={startWizard} disabled={hasErrors}>
                  ⚡ Start Generation Wizard
                </button>
                {hasSavedConfig && (
                  <div style={{ marginTop:10 }}>
                    <button type="button" className="btn btn-ghost"
                      style={{ width:'100%', justifyContent:'center', fontSize:12, fontWeight:600, padding:'9px 0',
                        border:'1px solid var(--border)', gap:6 }}
                      onClick={() => { loadSavedConfig(selSeries); startWizard(); }}
                      disabled={hasErrors}>
                      ♻️ Load Last Config &amp; Start Wizard
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* WIZARD */}
            {(wizardPhase==='wizard'||wizardPhase==='generating'||wizardPhase==='done') && (
              <div>

                {/* STEP 1 */}
                <div className="card" style={{ padding:'14px 16px', marginBottom:12 }}>
                  <StepHeader number={1} title="Conditions Review" subtitle="Confirm each condition before generating"
                    status={step1Complete?'done':wizardStep===1?'active':'locked'} />
                  {wizardStep>=1 && (
                    <>
                      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
                        <div style={{ flex:1, height:5, background:'var(--border)', borderRadius:3, overflow:'hidden' }}>
                          <div style={{ height:'100%', width:`${totalCondCount>0?(confirmedCount/totalCondCount)*100:0}%`, background:'var(--accent)', borderRadius:3, transition:'width .3s' }} />
                        </div>
                        <span style={{ fontSize:11, color:'var(--mid)', whiteSpace:'nowrap' }}>{confirmedCount} / {totalCondCount}</span>
                      </div>

                      {/* Auto-confirmed chips */}
                      {conditions.filter(c=>AUTO_CONFIRM_TYPES.has(c.condition_type)).length>0 && (
                        <div style={{ padding:'6px 10px', borderRadius:6, marginBottom:8, background:'rgba(107,114,128,.04)', border:'1px solid var(--border)' }}>
                          <div style={{ fontSize:9, color:'var(--dim)', fontWeight:700, textTransform:'uppercase', marginBottom:4 }}>Auto-confirmed</div>
                          <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                            {conditions.filter(c=>AUTO_CONFIRM_TYPES.has(c.condition_type)).map(c=>(
                              <span key={c.id} style={{ fontSize:9, padding:'2px 7px', borderRadius:10, background:'rgba(0,200,150,.08)', color:'var(--accent)', border:'1px solid rgba(0,200,150,.2)' }}>
                                ✓ {conditionCode(c.condition_type)} · {describeCondition(c).substring(0,38)}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {[...BULK_TYPES].map(t=><BulkCondCard key={t} type={t} />)}
                      {individualConds.map((cond,i)=><CondCard key={cond.id} cond={cond} index={i} />)}

                      {/* No Game Days */}
                      <SectionCard sectionKey="section_noGameDays" title="No Game Days" icon="🚫">
                        <div style={{ fontSize:10, color:'var(--dim)', marginBottom:8 }}>Block specific dates from being scheduled — by division or by individual team</div>
                        {/* Tab toggle */}
                        <div style={{ display:'flex', gap:4, marginBottom:8 }}>
                          {['division','team'].map(mode => (
                            <button key={mode} type="button"
                              onClick={e=>{ e.stopPropagation(); setNgMode(mode); }}
                              style={{ fontSize:10, padding:'3px 10px', borderRadius:4, cursor:'pointer', border:'1px solid',
                                background: ngMode===mode ? 'var(--accent)' : 'var(--surface)',
                                color: ngMode===mode ? 'white' : 'var(--mid)',
                                borderColor: ngMode===mode ? 'var(--accent)' : 'var(--border)' }}>
                              {mode === 'division' ? '📋 Per Division' : '👤 Per Team'}
                            </button>
                          ))}
                        </div>

                        {/* Existing entries */}
                        {[...noGameDays.map(ng=>({...ng,_type:'div'})), ...noGameDaysTeam.map(ng=>({...ng,_type:'team'}))].length > 0 && (
                          <div style={{ marginBottom:8 }}>
                            {noGameDays.map((ng,i)=>(
                              <div key={ng.id} style={{ display:'flex', alignItems:'center', gap:6,
                                  padding:'3px 0', borderBottom:'1px solid var(--border)', fontSize:11 }}>
                                <span style={{ fontSize:9, padding:'1px 5px', borderRadius:3,
                                  background:'rgba(74,144,217,.12)', color:'var(--blue)', fontWeight:700 }}>DIV</span>
                                <span style={{ fontWeight:600, color:'var(--text)', flex:1 }}>{ng.divisionName}</span>
                                <span style={{ color:'var(--mid)', fontFamily:"'Roboto Mono',monospace", fontSize:10 }}>{ng.date}</span>
                                <button type="button" style={{ fontSize:11, color:'var(--red)', background:'none', border:'none', cursor:'pointer', padding:'0 3px' }}
                                  onClick={e=>{ e.stopPropagation(); setNoGameDays(p=>p.filter((_,j)=>j!==i)); }}>✕</button>
                              </div>
                            ))}
                            {noGameDaysTeam.map((ng,i)=>(
                              <div key={ng.id} style={{ display:'flex', alignItems:'center', gap:6,
                                  padding:'3px 0', borderBottom:'1px solid var(--border)', fontSize:11 }}>
                                <span style={{ fontSize:9, padding:'1px 5px', borderRadius:3,
                                  background:'rgba(155,109,255,.12)', color:'var(--purple)', fontWeight:700 }}>TEAM</span>
                                <span style={{ fontWeight:600, color:'var(--text)', flex:1 }}>{ng.teamShort}</span>
                                <span style={{ color:'var(--mid)', fontFamily:"'Roboto Mono',monospace", fontSize:10 }}>{ng.date}</span>
                                <button type="button" style={{ fontSize:11, color:'var(--red)', background:'none', border:'none', cursor:'pointer', padding:'0 3px' }}
                                  onClick={e=>{ e.stopPropagation(); setNoGameDaysTeam(p=>p.filter((_,j)=>j!==i)); }}>✕</button>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Add form — Per Division */}
                        {ngMode === 'division' && (
                          <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                            <select className="input select" style={{ fontSize:11, flex:'1 1 120px' }}
                              value={ngDiv} onChange={e=>setNgDiv(e.target.value)}>
                              <option value="">Division…</option>
                              {divisions.map(d=><option key={d.id} value={d.id}>{d.name}</option>)}
                            </select>
                            <DateInput style={{ flex:'1 1 150px' }}
                              value={ngDate}
                              onChange={v=>setNgDate(v)} />
                            <button type="button" className="btn btn-primary btn-sm" style={{ fontSize:11 }}
                              disabled={!ngDiv || !ngDate}
                              onClick={e=>{ e.stopPropagation();
                                const div = divisions.find(d=>d.id===ngDiv);
                                setNoGameDays(p=>[...p,{ id:`ng-${Date.now()}`, divisionId:ngDiv, divisionName:div?.name||ngDiv, date:ngDate.trim() }]);
                                setNgDiv(''); setNgDate('');
                              }}>+ Add</button>
                          </div>
                        )}

                        {/* Add form — Per Team */}
                        {ngMode === 'team' && (
                          <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                            <select className="input select" style={{ fontSize:11, flex:'1 1 120px' }}
                              value={ngtTeam} onChange={e=>setNgtTeam(e.target.value)}>
                              <option value="">Team…</option>
                              {[...teams].sort((a,b)=>(a.short_name||'').localeCompare(b.short_name||'')).map(t=>(
                                <option key={t.id} value={t.id}>{t.short_name||t.name}</option>
                              ))}
                            </select>
                            <DateInput style={{ flex:'1 1 150px' }}
                              value={ngtDate}
                              onChange={v=>setNgtDate(v)} />
                            <button type="button" className="btn btn-primary btn-sm" style={{ fontSize:11 }}
                              disabled={!ngtTeam || !ngtDate}
                              onClick={e=>{ e.stopPropagation();
                                const t = teams.find(x=>x.id===ngtTeam);
                                setNoGameDaysTeam(p=>[...p,{ id:`ngt-${Date.now()}`, teamId:ngtTeam, teamShort:t?.short_name||ngtTeam, date:ngtDate.trim() }]);
                                setNgtTeam(''); setNgtDate('');
                              }}>+ Add</button>
                          </div>
                        )}
                        <div style={{ fontSize:9, color:'var(--dim)', marginTop:4 }}>
                          Select date using the date picker
                        </div>
                      </SectionCard>

                      {/* Mandatory Games */}
                      <SectionCard sectionKey="section_mandatory" title="Mandatory Games" icon="📌">
                        <div style={{ fontSize:10, color:'var(--dim)', marginBottom:6 }}>Force specific fixtures to be scheduled</div>
                        {mandatoryGames.map((mg,i)=>(
                          <div key={mg.id} style={{ display:'flex', alignItems:'center', gap:6, padding:'3px 0', borderBottom:'1px solid var(--border)', fontSize:11 }}>
                            <span>📌</span>
                            <span style={{ fontWeight:600, color:'var(--text)' }}>{mg.homeTeam} vs {mg.awayTeam}</span>
                            <span style={{ color:'var(--dim)', fontSize:10 }}>{mg.divisionName} · {mg.date}{mg.groundName?' · '+mg.groundName:''}</span>
                            <button type="button" style={{ fontSize:11, color:'var(--red)', background:'none', border:'none', cursor:'pointer', marginLeft:'auto', padding:'0 3px' }}
                              onClick={e=>{ e.stopPropagation(); setMandatoryGames(p=>p.filter((_,j)=>j!==i)); }}>✕</button>
                          </div>
                        ))}
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6, marginTop:6 }}>
                          <select className="input select" style={{ fontSize:11 }} value={mgDiv} onChange={e=>{ setMgDiv(e.target.value); setMgHome(''); setMgAway(''); }}>
                            <option value="">Division…</option>
                            {divisions.map(d=><option key={d.id} value={d.id}>{d.name}</option>)}
                          </select>
                          <select className="input select" style={{ fontSize:11 }} value={mgHome} onChange={e=>setMgHome(e.target.value)}>
                            <option value="">Home team…</option>
                            {teams.filter(t=>!mgDiv||t.division_id===mgDiv).map(t=><option key={t.id} value={t.id}>{t.short_name||t.name}</option>)}
                          </select>
                          <select className="input select" style={{ fontSize:11 }} value={mgAway} onChange={e=>setMgAway(e.target.value)}>
                            <option value="">Away team…</option>
                            {teams.filter(t=>(!mgDiv||t.division_id===mgDiv)&&t.id!==mgHome).map(t=><option key={t.id} value={t.id}>{t.short_name||t.name}</option>)}
                          </select>
                          <DateInput style={{ flex:1 }}
                            value={mgDate}
                            onChange={v=>setMgDate(v)} />
                          <select className="input select" style={{ fontSize:11, gridColumn:'span 2' }} value={mgGround} onChange={e=>setMgGround(e.target.value)}>
                            <option value="">Ground (optional)…</option>
                            {grounds.map(g=><option key={g.id} value={g.id}>{g.name}</option>)}
                          </select>
                        </div>
                        <button type="button" className="btn btn-primary btn-sm" style={{ fontSize:11, marginTop:6 }}
                          disabled={!mgDiv||!mgHome||!mgAway||!mgDate}
                          onClick={e=>{ e.stopPropagation(); const div=divisions.find(d=>d.id===mgDiv),ht=teams.find(t=>t.id===mgHome),at=teams.find(t=>t.id===mgAway),gnd=grounds.find(g=>g.id===mgGround); setMandatoryGames(p=>[...p,{id:`mg-${Date.now()}`,divisionId:mgDiv,divisionName:div?.name||mgDiv,homeTeamId:mgHome,homeTeam:ht?.short_name||mgHome,awayTeamId:mgAway,awayTeam:at?.short_name||mgAway,date:mgDate,groundId:mgGround||null,groundName:gnd?.name||null}]); setMgDiv('');setMgHome('');setMgAway('');setMgDate('');setMgGround(''); }}>
                          + Add Game
                        </button>
                      </SectionCard>

                      {/* Conclude By */}
                      <SectionCard sectionKey="section_concludeBy" title="Schedule Must Conclude By" icon="📅">
                        <div style={{ fontSize:10, color:'var(--dim)', marginBottom:8 }}>
                          Latest Sunday each division's games must be scheduled by
                        </div>
                        <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                          {divisions.map(d => {
                            const val = concludeBy[d.id] || '';
                            return (
                              <div key={d.id} style={{ display:'flex', alignItems:'center', gap:8 }}>
                                <span style={{ fontSize:11, fontWeight:600, color:'var(--text)', minWidth:90, flexShrink:0 }}>{d.name}</span>
                                <DateInput style={{ flex:1 }}
                                  value={val}
                                  onChange={v => setConcludeBy(p=>({...p,[d.id]:v}))} />
                                {val && (
                                  <span style={{ fontSize:10, color:'var(--accent)', whiteSpace:'nowrap', flexShrink:0 }}>
                                    ✓ by {new Date(val+'T12:00:00Z').toLocaleDateString('en-US',{month:'short',day:'numeric'})}
                                  </span>
                                )}
                                {val && (
                                  <button type="button" style={{ fontSize:10, color:'var(--dim)', background:'none', border:'none', cursor:'pointer', flexShrink:0 }}
                                    onClick={e=>{ e.stopPropagation(); setConcludeBy(p=>({...p,[d.id]:''})); }}>✕</button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        <div style={{ fontSize:9, color:'var(--dim)', marginTop:6 }}>
                          Leave blank for no deadline. Copy-paste from spreadsheet supported.
                        </div>
                      </SectionCard>

                      {/* Max Gap Hard Fail per division */}
                      <SectionCard sectionKey="section_maxGapFail" title="Max Gap Hard Fail (per division)" icon="⛔">
                        <div style={{ fontSize:10, color:'var(--dim)', marginBottom:8, lineHeight:1.5 }}>
                          If any team in a division has a gap larger than this many days between games,
                          the generation <strong style={{ color:'var(--red)' }}>fails</strong> and the schedule is not saved.
                          Leave blank for no hard limit.
                          <br/>Recommended: target <strong>14 days</strong> (C13), hard fail at <strong>28 days</strong> (4 weeks).
                        </div>
                        <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                          {divisions.map(d => {
                            const val = maxGapFail[d.id] || '';
                            const valid = val === '' || /^\d+$/.test(val);
                            return (
                              <div key={d.id} style={{ display:'flex', alignItems:'center', gap:8 }}>
                                <span style={{ fontSize:11, fontWeight:600, color:'var(--text)', minWidth:90, flexShrink:0 }}>{d.name}</span>
                                <input type="text" className="input"
                                  placeholder="days (e.g. 28)"
                                  style={{ fontSize:11, fontFamily:"'Roboto Mono',monospace", width:90,
                                    borderColor: val && !valid ? 'var(--red)' : undefined }}
                                  defaultValue={val}
                                  onBlur={e => setMaxGapFail(p=>({...p,[d.id]:e.target.value}))}
                                  onPaste={e=>e.stopPropagation()} />
                                {val && valid && parseInt(val) > 0 && (
                                  <span style={{ fontSize:10, color:'var(--red)', fontWeight:700, flexShrink:0 }}>
                                    ⛔ fail if gap &gt; {Math.round(parseInt(val)/7)} weeks
                                  </span>
                                )}
                                {val && (!valid || parseInt(val) <= 0) && (
                                  <span style={{ fontSize:10, color:'var(--dim)', flexShrink:0 }}>use a number of days</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        <div style={{ fontSize:9, color:'var(--dim)', marginTop:6 }}>
                          Applies to all teams in the division. Uses calendar days between consecutive games.
                        </div>
                      </SectionCard>

                      {/* Extra conditions */}
                      {extraConds.length>0 && (
                        <div style={{ marginBottom:6 }}>
                          <div style={{ fontSize:9, fontWeight:700, color:'var(--dim)', textTransform:'uppercase', marginBottom:4 }}>Added conditions</div>
                          {extraConds.map((ec,i)=>(
                            <div key={ec.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 10px', borderRadius:6, marginBottom:3,
                                background:ec.confirmed?'rgba(0,200,150,.04)':'rgba(245,158,11,.04)',
                                border:`1px solid ${ec.confirmed?'rgba(0,200,150,.2)':'rgba(245,158,11,.3)'}` }}>
                              <span style={{ fontSize:11, flex:1, color:'var(--text)' }}>{ec.text}</span>
                              {!ec.confirmed
                                ? <button type="button" className="btn btn-sm" style={{ fontSize:10, background:'rgba(0,200,150,.1)', border:'1px solid rgba(0,200,150,.3)', color:'var(--accent)' }}
                                    onClick={e=>{ e.preventDefault(); e.stopPropagation(); setExtraConds(p=>p.map((x,j)=>j===i?{...x,confirmed:true}:x)); }}>✅</button>
                                : <span style={{ fontSize:10, color:'var(--accent)', fontWeight:700 }}>✓</span>
                              }
                              <button type="button" style={{ fontSize:11, color:'var(--red)', background:'none', border:'none', cursor:'pointer' }}
                                onClick={e=>{ e.stopPropagation(); setExtraConds(p=>p.filter((_,j)=>j!==i)); }}>✕</button>
                            </div>
                          ))}
                        </div>
                      )}

                      {!addCondMode ? (
                        <div style={{ display:'flex', gap:6, marginTop:6 }}>
                          <button type="button" className="btn btn-ghost btn-sm" style={{ fontSize:11 }} onClick={()=>setAddCondMode('structured')}>+ Add structured condition</button>
                          <button type="button" className="btn btn-ghost btn-sm" style={{ fontSize:11 }} onClick={()=>setAddCondMode('freetext')}>+ Add free text note</button>
                        </div>
                      ) : addCondMode==='freetext' ? (
                        <div style={{ padding:'10px 12px', background:'var(--surface)', borderRadius:8, border:'1px solid var(--border)', marginTop:6 }}>
                          <textarea rows={2} className="input" placeholder="e.g. SCRP must not play before June"
                            style={{ width:'100%', fontSize:11, resize:'vertical', marginBottom:6, boxSizing:'border-box' }}
                            defaultValue={newCondText} onBlur={e=>setNewCondText(e.target.value)} />
                          <div style={{ display:'flex', gap:6 }}>
                            <button type="button" className="btn btn-primary btn-sm" style={{ fontSize:11 }}
                              onClick={e=>{ e.stopPropagation(); const txt=newCondText.trim(); if(!txt)return; setExtraConds(p=>[...p,{id:`extra-${Date.now()}`,text:txt,confirmed:false}]); setNewCondText(''); setAddCondMode(null); }}>Add</button>
                            <button type="button" className="btn btn-ghost btn-sm" style={{ fontSize:11 }}
                              onClick={e=>{ e.stopPropagation(); setAddCondMode(null); setNewCondText(''); }}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ padding:'10px 12px', background:'var(--surface)', borderRadius:8, border:'1px solid var(--border)', marginTop:6 }}>
                          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:8 }}>
                            <div>
                              <div style={{ fontSize:9, color:'var(--dim)', marginBottom:2 }}>Type</div>
                              <select className="input select" style={{ fontSize:11, width:'100%' }} value={newCondType} onChange={e=>setNewCondType(e.target.value)}>
                                <option value="">Select…</option>
                                <option value="bye_max">C4 · Max consecutive byes</option>
                                <option value="ground_booking">C10 · Home ground assignment</option>
                                <option value="team_ground_limit">C8 · Team ground limit</option>
                                <option value="fixed_game_date">C11 · Fixed game date</option>
                              </select>
                            </div>
                            <div>
                              <div style={{ fontSize:9, color:'var(--dim)', marginBottom:2 }}>Value</div>
                              <input className="input" style={{ fontSize:11, width:'100%' }} placeholder="e.g. max 3" defaultValue={newCondText} onBlur={e=>setNewCondText(e.target.value)} />
                            </div>
                          </div>
                          <div style={{ display:'flex', gap:6 }}>
                            <button type="button" className="btn btn-primary btn-sm" style={{ fontSize:11 }}
                              onClick={e=>{ e.stopPropagation(); if(!newCondText.trim())return; setExtraConds(p=>[...p,{id:`extra-${Date.now()}`,text:`${newCondType||'Condition'}: ${newCondText.trim()}`,confirmed:false}]); setNewCondType(''); setNewCondText(''); setAddCondMode(null); }}>Add</button>
                            <button type="button" className="btn btn-ghost btn-sm" style={{ fontSize:11 }}
                              onClick={e=>{ e.stopPropagation(); setAddCondMode(null); setNewCondType(''); setNewCondText(''); }}>Cancel</button>
                          </div>
                        </div>
                      )}

                      <div style={{ display:'flex', gap:8, marginTop:12 }}>
                        <button type="button" className="btn btn-ghost btn-sm" style={{ fontSize:11 }}
                          onClick={e=>{ e.preventDefault(); e.stopPropagation();
                            const all={};
                            individualConds.forEach(c=>{all[c.id]='confirmed';});
                            bulkTypesPresent.forEach(t=>{all[`bulk_${t}`]='confirmed';});
                            all['section_noGameDays']='confirmed'; all['section_mandatory']='confirmed';
            all['section_concludeBy']='confirmed'; all['section_maxGapFail']='confirmed';
                            setCondStatus(all); setExtraConds(p=>p.map(c=>({...c,confirmed:true})));
                            setCollapsed(prev=>{ const r={...prev}; individualConds.forEach(c=>{r[c.id]=true;}); return r; });
                          }}>
                          ✅ Confirm All
                        </button>
                        {wizardStep===1 && (
                          <button type="button" className="btn btn-primary" style={{ fontSize:12, fontWeight:700,
                            opacity: step1Complete ? 1 : 0.6 }}
                            onClick={e=>{ e.preventDefault(); e.stopPropagation(); setWizardStep(2); }}>
                            Next: Review Conflicts →
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* STEP 2 */}
                <div id="conflict-checklist" className="card" style={{ padding:'14px 16px', marginBottom:12, opacity:wizardStep<2?.45:1, pointerEvents:wizardStep<2?'none':'auto' }}>
                  <StepHeader number={2} title="Conflict Checklist" subtitle="Review pairs that couldn't find a slot"
                    status={wizardStep<2?'locked':step2Complete?'done':'active'} />
                  {wizardStep>=2 && (
                    <>
                      {knownConflicts.length===0 ? (
                        <div style={{ fontSize:12, color:'var(--accent)', fontWeight:600, padding:'8px 0' }}>✅ No known conflicts. Proceed to confirm.</div>
                      ) : (
                        <>
                          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
                            <span style={{ fontSize:11, color:'var(--mid)', flex:1 }}>
                              {knownConflicts.length} pair{knownConflicts.length!==1?'s':''} need resolution:
                            </span>
                            {resolvedCount > 0 && (
                              <button className="btn btn-sm" style={{ fontSize:10,
                                  background:'rgba(239,68,68,.1)', border:'1px solid rgba(239,68,68,.3)', color:'var(--red)' }}
                                onClick={() => setConflictResolutions({})}>
                                ↺ Reset All
                              </button>
                            )}
                            <button className="btn btn-sm" style={{ fontSize:10,
                                background:'rgba(245,158,11,.1)', border:'1px solid rgba(245,158,11,.3)', color:'var(--gold)' }}
                              onClick={() => {
                                const all = {};
                                knownConflicts.forEach(c => { all[`${c.home_id||c.home}:${c.away_id||c.away}`] = 'flag'; });
                                setConflictResolutions(p => ({...p,...all}));
                              }}>
                              🚩 Flag All
                            </button>
                            <button className="btn btn-sm" style={{ fontSize:10,
                                background:'rgba(107,114,128,.1)', border:'1px solid rgba(107,114,128,.3)', color:'var(--mid)' }}
                              onClick={() => {
                                const all = {};
                                knownConflicts.forEach(c => { all[`${c.home_id||c.home}:${c.away_id||c.away}`] = 'skip'; });
                                setConflictResolutions(p => ({...p,...all}));
                              }}>
                              ⏭ Skip All
                            </button>
                          </div>
                          {knownConflicts.map(cf=>{
                            const key=`${cf.home_id||cf.home}:${cf.away_id||cf.away}`;
                            const res=conflictResolutions[key]; const ad=assignData[key]||{};
                            const assignedSummary = res==='assign' && ad.date
                              ? `${ad.date} · ${grounds.find(g=>g.id===ad.ground_id)?.name || ''} · ${ad.time || ''}`
                              : '';
                            return (
                              <div key={key} style={{ padding:'10px 12px', borderRadius:8, marginBottom:6,
                                  background:res==='skip'?'rgba(107,114,128,.06)':res==='flag'?'rgba(245,158,11,.06)':res==='assign'?'rgba(0,200,150,.06)':'var(--surface)',
                                  border:`1px solid ${res?'rgba(0,200,150,.25)':'rgba(239,68,68,.25)'}` }}>
                                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                                  <span style={{ fontSize:11, color:'var(--red)', fontWeight:700 }}>✕</span>
                                  <span style={{ fontSize:12, fontWeight:700, color:'var(--text)', flex:1 }}>{cf.home||cf.home_short} vs {cf.away||cf.away_short}</span>
                                  {cf.reason && <span style={{ fontSize:10, color:'var(--dim)' }}>{cf.reason}</span>}
                                  {res && <span style={{ fontSize:10, color:'var(--accent)', fontWeight:700 }}>{res==='assign'?'📅 Assigned':res==='skip'?'⏭ Skipped':'🚩 Flagged'}</span>}
                                </div>
                                {res==='assign' && assignedSummary && (
                                  <div style={{ fontSize:10, color:'var(--dim)', marginTop:4, fontFamily:"'Roboto Mono', monospace" }}>{assignedSummary}</div>
                                )}
                                {!res && (
                                  <div style={{ display:'flex', gap:5, marginTop:6 }}>
                                    <button type="button" className="btn btn-sm" style={{ fontSize:10, background:'rgba(74,144,217,.1)', border:'1px solid rgba(74,144,217,.3)', color:'var(--blue)' }}
                                      onClick={e=>{ e.stopPropagation(); setAssignModalKey(key); }}>📅 Assign</button>
                                    <button type="button" className="btn btn-sm" style={{ fontSize:10, background:'rgba(107,114,128,.1)', border:'1px solid rgba(107,114,128,.3)', color:'var(--mid)' }}
                                      onClick={e=>{ e.stopPropagation(); setConflictResolutions(p=>({...p,[key]:'skip'})); }}>⏭ Skip</button>
                                    <button type="button" className="btn btn-sm" style={{ fontSize:10, background:'rgba(245,158,11,.1)', border:'1px solid rgba(245,158,11,.3)', color:'var(--gold)' }}
                                      onClick={e=>{ e.stopPropagation(); setConflictResolutions(p=>({...p,[key]:'flag'})); }}>🚩 Flag</button>
                                  </div>
                                )}
                                {res && <button type="button" className="btn btn-ghost btn-sm" style={{ fontSize:10, marginTop:4 }}
                                  onClick={e=>{ e.stopPropagation(); setConflictResolutions(p=>({...p,[key]:undefined})); setAssignData(p=>({...p,[key]:{}})); }}>↺ Change</button>}
                              </div>
                            );
                          })}
                          <div style={{ padding:'5px 10px', background:'var(--surface)', borderRadius:5, border:'1px solid var(--border)', fontSize:11, color:'var(--mid)', marginTop:5 }}>
                            Expected: <strong style={{ color:'var(--text)' }}>{expectedTotal}</strong> ·
                            <span style={{ color:'var(--accent)' }}> {Object.values(conflictResolutions).filter(v=>v==='assign').length} assigned</span> ·
                            <span style={{ color:'var(--dim)' }}> {Object.values(conflictResolutions).filter(v=>v==='skip').length} skipped</span> ·
                            <span style={{ color:'var(--gold)' }}> {Object.values(conflictResolutions).filter(v=>v==='flag').length} flagged</span>
                          </div>
                        </>
                      )}
                      {wizardStep===2 && !genResult && (
                        <button type="button" className="btn btn-primary"
                          disabled={!step2Complete}
                          style={{ fontSize:12, fontWeight:700, marginTop:10,
                            opacity: step2Complete ? 1 : 0.6,
                            cursor: step2Complete ? 'pointer' : 'not-allowed' }}
                          onClick={e=>{ e.preventDefault(); e.stopPropagation(); if (step2Complete) setWizardStep(3); }}>
                          Next: Final Confirmation →
                        </button>
                      )}
                    </>
                  )}
                </div>

                {/* STEP 3 */}
                <div className="card" style={{ padding:'14px 16px', marginBottom:12, opacity:wizardStep<3?.45:1, pointerEvents:wizardStep<3?'none':'auto' }}>
                  <StepHeader number={3} title="Confirm & Generate" subtitle="Review summary then generate"
                    status={wizardStep<3?'locked':wizardPhase==='done'?'done':'active'} />
                  {wizardStep>=3 && wizardPhase!=='generating' && wizardPhase!=='done' && (
                    <>
                      <div style={{ padding:'10px 14px', background:'var(--surface)', borderRadius:8, border:'1px solid var(--border)', marginBottom:14 }}>
                        <div style={{ fontSize:11, fontWeight:700, color:'var(--text)', marginBottom:8 }}>Summary</div>
                        <div style={{ display:'flex', flexDirection:'column', gap:4, fontSize:11, color:'var(--mid)' }}>
                          <div>✅ <strong style={{ color:'var(--text)' }}>{confirmedCount}</strong> conditions confirmed{extraConds.length>0?` (+${extraConds.length} added)`:''}</div>
                          <div>📋 Expected <strong style={{ color:'var(--text)' }}>{expectedTotal}</strong> fixtures ({expectedLeague} league{expectedSeeded>0?` + ${expectedSeeded} seeded`:''})</div>
                          {perDivExpected.map(d=>(
                            <div key={d.name} style={{ paddingLeft:14 }}>
                              {d.name}: <strong style={{ color:'var(--text)' }}>{d.league}</strong>{d.seeded>0&&<span style={{ color:'var(--blue)' }}> +{d.seeded} seeded</span>}
                              {concludeBy[d.id]&&<span style={{ color:'var(--gold)' }}> · conclude by {new Date(concludeBy[d.id]+'T12:00:00Z').toLocaleDateString('en-US',{month:'short',day:'numeric'})}</span>}
                            </div>
                          ))}
                          {(noGameDays.length + noGameDaysTeam.length) > 0 && (
                            <div>🚫 {noGameDays.length} division no-game date{noGameDays.length!==1?'s':''}{noGameDaysTeam.length>0?`, ${noGameDaysTeam.length} team no-game date${noGameDaysTeam.length!==1?'s':''}`:''}</div>
                          )}
                          {mandatoryGames.length>0 && <div>📌 {mandatoryGames.length} mandatory game(s) added</div>}
                          {Object.entries(maxGapFail).filter(([,v])=>v&&parseInt(v)>0).length > 0 && (
                            <div>⛔ Hard fail if gap &gt; {Object.entries(maxGapFail).filter(([,v])=>v&&parseInt(v)>0).map(([id,days])=>{
                              const d=divisions.find(x=>x.id===id); return `${(d?.name||id).replace('Division ','D')}: ${days}d`;
                            }).join(', ')}</div>
                          )}
                          {knownConflicts.length>0 && <div>{Object.values(conflictResolutions).filter(v=>v==='assign').length} assigned · {Object.values(conflictResolutions).filter(v=>v==='skip').length} skipped · {Object.values(conflictResolutions).filter(v=>v==='flag').length} flagged</div>}
                          <div style={{ marginTop:4, fontSize:10, color:'var(--dim)' }}>Order: {divOrder.join(' → ')}</div>
                        </div>
                      </div>
                      <button type="button" className="btn btn-primary"
                        style={{ width:'100%', justifyContent:'center', fontSize:14, fontWeight:700, padding:'12px 0' }}
                        onClick={handleGenerate}>
                        ⚡ Confirm & Generate Schedule
                      </button>
                      <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:6 }}>
                        <button type="button" className="btn btn-ghost btn-sm" style={{ fontSize:11 }}
                          onClick={e=>{ e.stopPropagation(); saveConfig(true); }}>
                          💾 Save config
                        </button>
                        {hasSavedConfig && (
                          <span style={{ fontSize:10, color:'var(--accent)' }}>✓ saved</span>
                        )}
                      </div>
                      <button type="button" className="btn btn-ghost btn-sm" style={{ fontSize:11, marginTop:8 }}
                        onClick={e=>{ e.preventDefault(); setWizardPhase('idle'); setWizardStep(1); setCondStatus({}); setCollapsed({}); setExtraConds([]); }}>
                        ← Start over
                      </button>
                    </>
                  )}
                  {wizardPhase==='generating' && (
                    <div style={{ display:'flex', alignItems:'center', gap:10, padding:'12px 0', color:'var(--mid)', fontSize:12 }}>
                      <Spinner size={16}/> Generating schedule…
                    </div>
                  )}
                </div>

                {/* Log & Results */}
                {(genLog.length>0||genResult) && (
                  <div className="card" style={{ padding:'14px 16px' }}>
                    <div style={{ fontSize:11, fontWeight:800, color:'var(--dim)', textTransform:'uppercase', letterSpacing:'.4px', marginBottom:8 }}>Generation Log</div>
                    <div style={{ maxHeight:200, overflowY:'auto', background:'var(--surface)', borderRadius:5, padding:'5px 10px', border:'1px solid var(--border)', marginBottom:12 }}>
                      {genLog.map((e,i)=>{
                        const cols={ok:'var(--accent)',warn:'var(--gold)',error:'var(--red)',info:'var(--mid)'};
                        const ics={ok:'✓',warn:'⚠',error:'✕',info:'·'};
                        return (<div key={i} style={{ display:'flex', gap:8, padding:'2px 0', borderBottom:'1px solid var(--border)', fontSize:11 }}>
                          <span style={{ color:cols[e.type]||'var(--mid)', fontWeight:700, flexShrink:0 }}>{ics[e.type]||'·'}</span>
                          <span style={{ color:e.type==='error'?'var(--red)':e.type==='warn'?'var(--gold)':'var(--mid)', lineHeight:1.5 }}>{e.msg}</span>
                        </div>);
                      })}
                      <div ref={logEndRef} />
                    </div>
                    {genResult && (
                      <>
                        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8, marginBottom:10 }}>
                          {[
                            {label:'Fixtures', value:genResult.total_fixtures||0, color:'var(--accent)'},
                            {label:'Conflicts', value:genResult.conflicts?.length||0, color:(genResult.conflicts?.length||0)>0?'var(--red)':'var(--accent)'},
                            {label:'Errors', value:genResult.errors?.length||0, color:(genResult.errors?.length||0)>0?'var(--red)':'var(--accent)'},
                            {label:'Warnings', value:genResult.warnings?.length||0, color:(genResult.warnings?.length||0)>0?'var(--gold)':'var(--accent)'},
                          ].map(s=>(
                            <div key={s.label} style={{ textAlign:'center', padding:'7px', background:'var(--surface)', borderRadius:5, border:'1px solid var(--border)' }}>
                              <div style={{ fontSize:9, color:'var(--dim)', fontWeight:700, textTransform:'uppercase' }}>{s.label}</div>
                              <div style={{ fontSize:20, fontWeight:800, color:s.color, fontFamily:"'Roboto Mono',monospace" }}>{s.value}</div>
                            </div>
                          ))}
                        </div>
                        {/* Hard fail — gap violations */}
                        {genResult.failed && genResult.gap_violations?.length > 0 && (
                          <div style={{ padding:'12px 14px', borderRadius:8, marginBottom:10,
                              background:'rgba(239,68,68,.08)', border:'2px solid rgba(239,68,68,.4)' }}>
                            <div style={{ fontSize:13, fontWeight:800, color:'var(--red)', marginBottom:6 }}>
                              ⛔ Generation Failed — Max Gap Violations
                            </div>
                            <div style={{ fontSize:11, color:'var(--mid)', marginBottom:8 }}>
                              Schedule NOT saved. Fix ground availability or increase the gap threshold and regenerate.
                            </div>
                            {genResult.gap_violations.map((v,i) => (
                              <div key={i} style={{ fontSize:10, color:'var(--red)', padding:'2px 0',
                                  borderBottom:'1px solid rgba(239,68,68,.15)', fontFamily:"'Roboto Mono',monospace" }}>
                                {v.division}: {v.message}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Downloads — only shown if generation succeeded */}
                        {!genResult.failed && (
                        <div style={{ display:'flex', gap:8, marginBottom:8 }}>
                          <button type="button" className="btn btn-primary" style={{ flex:1, fontSize:12 }} onClick={()=>downloadExcel(false)}>📊 By Division</button>
                          <button type="button" className="btn btn-primary" style={{ flex:1, fontSize:12 }} onClick={()=>downloadExcel(true)}>📋 All Divisions</button>
                          <button type="button" className="btn btn-ghost" style={{ fontSize:12 }} onClick={()=>navigate('/schedule')}>View Schedule →</button>
                        </div>
                        )}
                        {(genResult.conflicts?.length||0)>0 && (
                          <div style={{ padding:'10px 12px', background:'rgba(239,68,68,.05)', border:'1px solid rgba(239,68,68,.2)', borderRadius:7, fontSize:11, color:'var(--mid)' }}>
                            <div style={{ color:'var(--red)', fontWeight:700, marginBottom:5 }}>✕ {genResult.conflicts.length} conflict{genResult.conflicts.length!==1?'s':''} remain</div>
                            {genResult.conflicts.map((cf,i)=>(
                              <div key={i} style={{ padding:'2px 0', borderBottom:'1px solid rgba(239,68,68,.1)' }}>{cf.home} vs {cf.away}{cf.reason?` — ${cf.reason}`:''}</div>
                            ))}
                            <button type="button" className="btn btn-primary btn-sm" style={{ fontSize:11, marginTop:6 }}
                              onClick={()=>{ setKnownConflicts(genResult.conflicts); setConflictResolutions({}); setWizardPhase('wizard'); setWizardStep(2); setTimeout(()=>{ const el=document.getElementById('conflict-checklist'); if(el) el.scrollIntoView({behavior:'smooth',block:'start'}); },100); }}>
                              → Resolve in wizard
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

              </div>
            )}
          </div>{/* end right */}
        </div>
      )}

      {assignModalKey && (() => {
        const cf = knownConflicts.find(c => `${c.home_id||c.home}:${c.away_id||c.away}` === assignModalKey);
        if (!cf) return null;
        const divName = cf.division_name || divisions.find(d => d.id === cf.div_id)?.name || '';
        const homeTeam = teams.find(t => t.id === cf.home_id) || { id: cf.home_id, short_name: cf.home_short || cf.home, name: cf.home };
        const awayTeam = teams.find(t => t.id === cf.away_id) || { id: cf.away_id, short_name: cf.away_short || cf.away, name: cf.away };
        return (
          <AssignConflictModal
            homeTeam={homeTeam}
            awayTeam={awayTeam}
            divisionName={divName}
            allFixtures={allFixtures}
            grounds={grounds}
            teams={teams}
            groundAvailability={seriesAvailability}
            blackoutDates={[
              // Division-level no-game-days for this conflict's division
              ...noGameDays.filter(n => n.divisionId === cf.div_id).map(n => n.date),
              // Team-level no-game-days for either team
              ...noGameDaysTeam.filter(n => n.teamId === homeTeam.id || n.teamId === awayTeam.id).map(n => n.date),
            ]}
            onCancel={() => { setAssignModalKey(null); setFreedDate(null); }}
            onSave={(draft) => {
              setAssignData(p => ({ ...p, [assignModalKey]: draft }));
              setConflictResolutions(p => ({ ...p, [assignModalKey]: 'assign' }));
              setAssignModalKey(null);
              setFreedDate(null);
            }}
            onRescheduleFixture={(fix) => setRescheduleModal(fix)}
            preselectDate={freedDate}
          />
        );
      })()}

      {rescheduleModal && (
        <AssignConflictModal
          title={`↻ Reschedule ${rescheduleModal.busyTeamName} vs ${rescheduleModal.opponentName}`}
          homeTeam={teams.find(t => t.id === rescheduleModal.home_team_id) ||
            { id: rescheduleModal.home_team_id, short_name: rescheduleModal.busyTeamName }}
          awayTeam={teams.find(t => t.id === rescheduleModal.away_team_id) ||
            { id: rescheduleModal.away_team_id, short_name: rescheduleModal.opponentName }}
          divisionName={rescheduleModal.division_name}
          fixtureId={rescheduleModal.id}
          allFixtures={allFixtures}
          grounds={grounds}
          teams={teams}
          groundAvailability={seriesAvailability}
          blackoutDates={[
            ...noGameDays.filter(n => n.divisionId === rescheduleModal.division_id).map(n => n.date),
            ...noGameDaysTeam
              .filter(n => n.teamId === rescheduleModal.home_team_id || n.teamId === rescheduleModal.away_team_id)
              .map(n => n.date),
          ]}
          onCancel={() => setRescheduleModal(null)}
          onSave={async (draft) => {
            if (!draft.date || !draft.ground_id) { toast.error('Pick date and ground'); return; }
            try {
              const freed = rescheduleModal.match_date; // remember BEFORE we overwrite
              await fixturesAPI.update(rescheduleModal.id, {
                home_team_id: rescheduleModal.home_team_id,
                away_team_id: rescheduleModal.away_team_id,
                ground_id: draft.ground_id,
                match_date: draft.date,
                match_time: draft.time || '12:30',
                match_type: rescheduleModal.match_type || 'League',
                round_number: rescheduleModal.round_number || 0,
                status: 'confirmed',
              });
              toast.success(`Fixture rescheduled — ${String(freed).split('T')[0]} is now free for the original conflict`);
              setRescheduleModal(null);
              setFreedDate(String(freed).split('T')[0]);
              // Refresh fixtures so parent modal sees the update
              try {
                const fx = await fixturesAPI.list({ series_id: selSeries });
                setAllFixtures(fx.data || []);
              } catch { /* non-fatal */ }
            } catch (err) { toast.error('Reschedule failed: ' + (err.response?.data?.error || err.message)); }
          }}
        />
      )}
    </div>
  );
}
